
import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Activity,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle,
  ChevronRight,
  ClipboardCheck,
  FileCheck,
  FileSignature,
  FileText,
  Handshake,
  House,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Search,
  Settings,
  ShieldCheck,
  Wallet,
  Users,
  Scale,
  Receipt,
  Clock,
  Gavel,
  FolderOpen,
  MapPin,
  PenTool,
  CircleDollarSign,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { logoutUser } from "../../store/reducers/slices/authSlice";
import { useAppDispatch, useAppSelector } from "../../store/store";
import { resolveRole, type Role } from "../../utils/resolveRole";

type MenuItem = {
  name: string;
  path: string;
  icon: LucideIcon;
};

type MenuGroup = {
  title: string;
  items: MenuItem[];
};

const commonGroups: MenuGroup[] = [
  {
    title: "Account",
    items: [
      {
        name: "KYC Status",
        path: "/user/kyc",
        icon: ShieldCheck,
      },
      {
        name: "Notifications",
        path: "/user/notifications",
        icon: Bell,
      },
      {
        name: "Messages",
        path: "/user/messages",
        icon: MessageSquare,
      },
      {
        name: "Settings",
        path: "/user/settings",
        icon: Settings,
      },
    ],
  },
];

const roleMenus: Record<Role, MenuGroup[]> = {
  seller: [
    {
      title: "Overview",
      items: [
        // { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Property Management",
      items: [
        { name: "My Properties", path: "/user/property", icon: Building2 },
        { name: "Create Listing", path: "/user/property/create", icon: House },
        { name: "Bookings & Viewings", path: "/user/bookings", icon: CalendarDays },
      ],
    },
    {
      title: "Sales & Transactions",
      items: [
        { name: "Agency Agreements", path: "/user/agency-agreements", icon: FileCheck },
        { name: "Offers & Negotiations", path: "/user/offers", icon: Handshake },
        { name: "Deal Rooms", path: "/user/deal-room", icon: FolderOpen },
        { name: "Contracts & Deeds", path: "/user/documents", icon: FileText },
      ],
    },
  ],

  landlord: [
    {
      title: "Overview",
      items: [
        { name: "Landlord Journey", path: "/user/landlord/journey", icon: Activity },
        { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Property Management",
      items: [
        { name: "My Properties", path: "/user/property", icon: Building2 },
        { name: "Create Listing", path: "/user/property/create", icon: House },
        { name: "Bookings & Viewings", path: "/user/property/my-bookings", icon: CalendarDays },
        { name: "Agency Agreements", path: "/user/property/agency-agreements", icon: FileCheck },
        { name: "Enquiries", path: "/user/landlord/enquiries", icon: MessageSquare },
      ],
    },
    {
      title: "Tenancy Management",
      items: [
        { name: "Tenancies", path: "/user/landlord/tenancies", icon: Users },
        { name: "Rent Wallets", path: "/user/landlord/rent-wallets", icon: Wallet },
        { name: "Rent Deposits", path: "/user/landlord/rent-deposits", icon: CircleDollarSign },
        { name: "Rent Receipts", path: "/user/landlord/rent-receipts", icon: Receipt },
        { name: "Rent Reminders", path: "/user/landlord/rent-reminders", icon: Clock },
        { name: "Notices", path: "/user/landlord/notices", icon: FileText },
      ],
    },
    {
      title: "Transactions",
      items: [
        { name: "Offers & Negotiations", path: "/user/landlord/transaction/offers", icon: Handshake },
        { name: "Letters of Intent", path: "/user/landlord/transaction/lois", icon: FileSignature },
        { name: "Deal Rooms", path: "/user/landlord/transaction/deal-room", icon: FolderOpen },
        { name: "Contracts & Deeds", path: "/user/landlord/transaction/contracts", icon: FileCheck },
      ],
    },
  ],

  buyer: [
    {
      title: "Overview",
      items: [
        { name: "Buyer Journey", path: "/user/buyer/journey", icon: Activity },
        // { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Property Discovery",
      items: [
        { name: "Discover Properties", path: "/user/buyer/discover-properties", icon: Search },
        { name: "My Enquiries", path: "/user/buyer/my-enquiries", icon: MessageSquare },
        { name: "Bookings & Viewings", path: "/user/buyer/bookings-viewings", icon: CalendarDays },
        { name: "Inspections", path: "/user/buyer/inspections", icon: MapPin },
      ],
    },
    {
      title: "Purchase Process",
      items: [
        { name: "My Offers", path: "/user/buyer/my-offers", icon: Handshake },
        { name: "Letters of Intent", path: "/user/buyer/lois", icon: FileSignature },
        { name: "Deal Rooms", path: "/user/buyer/deal-rooms", icon: FolderOpen },
        { name: "Due Diligence", path: "/user/buyer/due-diligence", icon: ClipboardCheck },
        { name: "Contracts & Deeds", path: "/user/buyer/contracts", icon: FileText },
      ],
    },
  ],

  tenant: [
    {
      title: "Overview",
      items: [
        { name: "Tenant Journey", path: "/user/tenant/journey", icon: Activity },
        // { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Find a Property",
      items: [
        { name: "Discover Properties", path: "/user/tenant/discover-properties", icon: Search },
        { name: "My Enquiries", path: "/user/tenant/my-enquiries", icon: MessageSquare },
        { name: "Bookings & Viewings", path: "/user/tenant/bookings-viewings", icon: CalendarDays },
      ],
    },
    {
      title: "My Tenancy",
      items: [
        { name: "Agreements", path: "/user/tenant/agreements", icon: FileCheck },
        { name: "My Tenancies", path: "/user/tenant/tenancies", icon: Users },
        { name: "Rent Wallet", path: "/user/tenant/rent-wallets", icon: Wallet },
        { name: "Rent Receipts", path: "/user/tenant/rent-receipts", icon: Receipt },
        { name: "Notices & Renewals", path: "/user/tenant/notices-renewals", icon: FileText },
      ],
    },
  ],

  agent: [
    {
      title: "Overview",
      items: [
        { name: "Agent Journey", path: "/user/agent/journey", icon: Activity },
        // { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Listings & Appointments",
      items: [
        { name: "Available Listings", path: "/user/agent/available-listings", icon: Search },
        { name: "My Assignments", path: "/user/agent/assignments", icon: Building2 },
        { name: "Agency Agreements", path: "/user/agent/agency-agreements", icon: FileCheck },
        { name: "Enquiries", path: "/user/agent/enquiries", icon: MessageSquare },
        { name: "Bookings & Inspections", path: "/user/agent/bookings", icon: CalendarDays },
      ],
    },
    {
      title: "Deals & Earnings",
      items: [
        { name: "Deal Rooms", path: "/user/agent/deal-rooms", icon: FolderOpen },
        { name: "Offers & Negotiations", path: "/user/agent/offers", icon: Handshake },
        { name: "Commissions", path: "/user/agent/commissions", icon: CircleDollarSign },
        { name: "Payouts", path: "/user/agent/payouts", icon: Wallet },
      ],
    },
  ],

  lawyer: [
    {
      title: "Overview",
      items: [
        { name: "Lawyer Journey", path: "/user/lawyer/journey", icon: Activity },
        // { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Legal Work",
      items: [
        { name: "Engagements", path: "/user/lawyer/engagements", icon: BriefcaseBusiness },
        { name: "Deal Rooms", path: "/user/lawyer/deal-room", icon: FolderOpen },
        { name: "Due Diligence", path: "/user/lawyer/due-diligence", icon: ClipboardCheck },
        { name: "Property Searches", path: "/user/lawyer/property-searches", icon: Search },
        { name: "Negotiations", path: "/user/lawyer/negotiations", icon: Handshake },
      ],
    },
    {
      title: "Legal Documents",
      items: [
        { name: "Contracts of Sale", path: "/user/lawyer/contracts", icon: FileText },
        { name: "Tenancy Agreements", path: "/user/lawyer/tenancy-agreements", icon: FileCheck },
        { name: "Digital Deeds", path: "/user/lawyer/deeds", icon: PenTool },
        { name: "My Documents", path: "/user/lawyer/documents", icon: FolderOpen },
      ],
    },
  ],

  valuer: [
    {
      title: "Overview",
      items: [
        { name: "Valuer Journey", path: "/user/valuer/journey", icon: Activity },
        // { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
      ],
    },
    {
      title: "Valuation Work",
      items: [
        { name: "Assigned Valuations", path: "/user/valuer/assigned-valuations", icon: ClipboardCheck },
        { name: "Valuation Reports", path: "/user/valuer/valuation-reports", icon: FileText },
        { name: "Deal Room Documents", path: "/user/valuer/deal-room-documents", icon: FolderOpen },
      ],
    },
  ],

  admin: [
    {
      title: "Administration",
      items: [
        { name: "Dashboard", path: "/user/dashboard", icon: LayoutDashboard },
        { name: "Users & Verification", path: "/user/admin/users", icon: Users },
        { name: "Listing Reviews", path: "/user/admin/listings", icon: Building2 },
        { name: "Agency Agreements", path: "/user/agency-agreements", icon: FileCheck },
        { name: "Transactions", path: "/user/admin/transactions", icon: Activity },
        { name: "Escrow & Finance", path: "/user/admin/finance", icon: Wallet },
        { name: "Reports", path: "/user/admin/reports", icon: FileText },
      ],
    },
  ],
};

export default function Sidebar({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (value: boolean) => void;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);

  const role = useMemo(
    () => resolveRole(user),
    [user]
  );

  const groups = useMemo(() => {
    const baseGroups = roleMenus[role];

    // Avoid duplicating shared links if a role menu is extended later.
    return [
      ...baseGroups,
      ...commonGroups.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            !baseGroups.some((baseGroup) =>
              baseGroup.items.some(
                (baseItem) => baseItem.path === item.path
              )
            )
        ),
      })),
    ].filter((group) => group.items.length > 0);
  }, [role]);

  const displayRole = role
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

  const handleLogout = () => {
    setOpen(false);
    dispatch(logoutUser());
    navigate("/login");
  };

  const isItemActiveOld = (path: string) => {
    if (path === "/user/dashboard") {
      return location.pathname === path;
    }

    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`)
    );
  };
  
const isItemActive = (path: string) => {
  const currentPath = location.pathname.replace(/\/+$/, "") || "/";
  const menuPath = path.replace(/\/+$/, "") || "/";

  // Exact match only: prevent parent routes from being active
  // when visiting nested routes.
  return currentPath === menuPath;
};


  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm md:hidden"
        />
      )}

      <motion.aside
        initial={false}
        animate={{ x: open ? 0 : undefined }}
        className={`
          fixed left-0 top-0 z-[60]
          flex h-dvh w-64 flex-col
          border-r border-white/10
          bg-[#111111] text-white
          transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0
        `}
      >
        {/* Brand / role */}
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-5 py-5">
          <div>
            <p className="text-lg font-bold tracking-wide">CASA</p>
            <p className="mt-1 text-xs capitalize text-gray-400">
              {displayRole} workspace
            </p>
          </div>

          <button
            type="button"
            aria-label="Close sidebar"
            onClick={() => setOpen(false)}
            className="rounded-lg p-2 text-gray-400 hover:bg-white/10 hover:text-white md:hidden"
          >
            <X size={20} />
          </button>
        </div>

        {/* Role-based navigation */}
        <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {groups.map((group) => (
            <section key={group.title}>
              <h2 className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">
                {group.title}
              </h2>

              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isItemActive(item.path);

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      //end
                      onClick={() => setOpen(false)}
                      aria-current={active ? "page" : undefined}
                      className={`
                        group flex items-center gap-3
                        rounded-lg px-3 py-2.5
                        text-sm transition-colors duration-200
                        ${
                          active
                            ? "bg-primary text-white"
                            : "text-gray-400 hover:bg-white/5 hover:text-white"
                        }
                      `}
                    >
                      <Icon size={18} className="shrink-0" />

                      <span className="flex-1">{item.name}</span>

                      {active && (
                        <ChevronRight size={15} className="shrink-0" />
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </section>
          ))}
        </nav>

        {/* Logout */}
        <div className="shrink-0 border-t border-white/10 p-3">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm text-gray-400 transition hover:bg-red-500/10 hover:text-red-400"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
}
